import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Mail, Shield } from 'lucide-react';

const OTPModal = ({ isOpen, onClose, onVerify, email, purpose, isLoading }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);

  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setOtp(newOtp);
    
    // Focus the next empty input or the last one
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    try {
      await onVerify(otpCode);
      setOtp(['', '', '', '', '', '']);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid OTP code');
    }
  };

  const purposeTitles = {
    login: 'Login Verification',
    wire_transfer: 'Wire Transfer Verification',
    transfer: 'Transaction Verification'
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <DialogTitle className="text-center text-xl">
            {purposeTitles[purpose] || 'Verification Required'}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-center text-sm text-muted-foreground">
              <div className="flex items-center justify-center gap-2 mt-2">
                <Mail className="w-4 h-4" />
                <span>We've sent a 6-digit code to</span>
              </div>
              <span className="font-medium text-gray-900 mt-1 block">{email}</span>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* OTP Input */}
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <Input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-12 h-12 text-center text-lg font-bold border-2 focus:border-blue-500"
                disabled={isLoading}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <p className="text-sm text-red-600 text-center">{error}</p>
          )}

          {/* Info */}
          <p className="text-sm text-gray-600 text-center">
            Code expires in 10 minutes. Didn't receive it?{' '}
            <button className="text-blue-600 hover:underline font-medium">
              Resend Code
            </button>
          </p>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={isLoading || otp.join('').length !== 6}
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OTPModal;
